import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import type { SetupTourStep } from '@/lib/setupModules';
import { useImprovementPlanTerm } from '@/hooks/useImprovementPlanTerm';
import { applyPlanTerm } from '@/lib/improvementPlanTerm';
import { useRewardsCurrency } from '@/hooks/useRewardsCurrency';
import { applyCurrencyName } from '@/lib/rewardsCurrency';

interface Props {
  steps: SetupTourStep[];
  onClose: () => void;
  // completed = true when the user reached the end; false when they closed early
  onEnd?: (completed: boolean) => void;
}

interface ClampedRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const TOOLTIP_WIDTH = 330;
// Keep the spotlight clear of the fixed header + setup bar.
const TOP_CHROME = 96;

// The real top chrome is dynamic: the setup/walkthrough bar is in-flow sticky
// and ~250px tall on mobile — a fixed constant let spotlight frames draw right
// through it (Ashley's leader run framed the tour bar itself). Measure it.
function topChromeBottom(): number {
  const bar = document.querySelector('[data-walkthrough-bar], [data-setup-bar]');
  const barBottom = bar ? bar.getBoundingClientRect().bottom : 0;
  return Math.max(TOP_CHROME, barBottom + 6);
}

type Phase = 'show' | 'await' | 'celebrate';

export default function SpotlightTour({ steps, onClose, onEnd }: Props) {
  const planTerm = useImprovementPlanTerm();
  const currency = useRewardsCurrency();
  const [available, setAvailable] = useState<SetupTourStep[] | null>(null);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('show');
  const [rect, setRect] = useState<ClampedRect | null>(null);
  const [doneDetail, setDoneDetail] = useState<unknown>(null);
  const [, forceTick] = useState(0);

  // Orient the user first: start every tour from the top of the page so the
  // setup bar and page header are in view before anything gets spotlighted.
  // On mobile the page scrolls inside .mobile-main, NOT the window — scroll
  // both, or phones silently ignore every tour scroll.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.querySelector('main.mobile-main')?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Interactive "your turn" phases hand the whole page to the user — some
  // demo actions (the leadership area wizard) are taller than the screen, so
  // the scroll lock MUST release while they work and re-engage after.
  const scrollLocked = !(phase === 'await' && !!available?.[index]?.interactive);

  // While guiding, the tour drives all scrolling. Lock the document with
  // overflow:hidden — the only approach that blocks EVERY user scroll path
  // (wheel, trackpad, scrollbar drag, keyboard, touch). overflow:hidden boxes
  // stay programmatically scrollable, so the tour's own scrolls keep working.
  // Radix dialogs opened during interactive steps manage their own body lock
  // and restore ours on close, so they're unaffected.
  useEffect(() => {
    if (!scrollLocked) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.getPropertyValue('overflow');
    const prevBody = body.style.getPropertyValue('overflow');
    // 'important' so this outranks stylesheet !important rules (the training
    // pages force body overflow-y auto on mobile in index.css).
    html.style.setProperty('overflow', 'hidden', 'important');
    body.style.setProperty('overflow', 'hidden', 'important');

    // On mobile the REAL scroller is .mobile-main (overflow-y auto) — locking
    // html/body alone leaves phones free-scrolling during the tour.
    const mobileMain = document.querySelector('main.mobile-main') as HTMLElement | null;
    const prevMain = mobileMain?.style.getPropertyValue('overflow') ?? '';
    mobileMain?.style.setProperty('overflow', 'hidden', 'important');

    // iOS Safari can still rubber-band an overflow:hidden page — belt and
    // suspenders with a touchmove block, leaving real dialogs scrollable.
    const insideDialog = (target: EventTarget | null) =>
      !!(target as Element | null)?.closest?.('[role="dialog"],[data-radix-popper-content-wrapper]');
    const onTouchMove = (e: TouchEvent) => {
      if (insideDialog(e.target)) return;
      e.preventDefault();
    };
    const opts = { passive: false, capture: true } as AddEventListenerOptions;
    window.addEventListener('touchmove', onTouchMove, opts);
    return () => {
      if (prevHtml) html.style.setProperty('overflow', prevHtml);
      else html.style.removeProperty('overflow');
      if (prevBody) body.style.setProperty('overflow', prevBody);
      else body.style.removeProperty('overflow');
      if (mobileMain) {
        if (prevMain) mobileMain.style.setProperty('overflow', prevMain);
        else mobileMain.style.removeProperty('overflow');
      }
      window.removeEventListener('touchmove', onTouchMove, opts);
    };
  }, [scrollLocked]);

  // Module pages lazy-load and fetch data — poll briefly for anchors before
  // giving up. Steps whose anchor never appears are simply skipped.
  useEffect(() => {
    let tries = 0;
    let timer = 0;
    const resolve = () => {
      const found = steps.filter((s) => document.querySelector(`[data-tour="${s.anchor}"]`));
      if (found.length === steps.length || (found.length && tries >= 6)) {
        setAvailable(found);
        return;
      }
      if (++tries < 15) timer = window.setTimeout(resolve, 300);
      else if (found.length) setAvailable(found);
      else onClose();
    };
    timer = window.setTimeout(resolve, 200);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps]);

  const step = available?.[index];

  // Each step starts in 'await' mode when interactive, else plain 'show'.
  useEffect(() => {
    setPhase(step?.interactive ? 'await' : 'show');
    setDoneDetail(null);
  }, [step]);

  // Interactive detection. Preferred: the page dispatches a doneEvent on REAL
  // success (a click may only open a dialog the user then cancels). Fallback
  // for steps without a doneEvent: a click inside the spotlighted element.
  useEffect(() => {
    if (phase !== 'await' || !step?.interactive) return;
    const doneEvent = step.interactive.doneEvent;
    if (doneEvent) {
      const onDone = (e: Event) => {
        setDoneDetail((e as CustomEvent).detail ?? null);
        window.setTimeout(() => setPhase('celebrate'), 650);
      };
      window.addEventListener(doneEvent, onDone, { once: true });
      return () => window.removeEventListener(doneEvent, onDone);
    }
    const el = document.querySelector(`[data-tour="${step.anchor}"]`);
    if (!el) return;
    const onDidIt = () => {
      window.setTimeout(() => setPhase('celebrate'), 650);
    };
    el.addEventListener('click', onDidIt, { capture: true, once: true });
    return () => el.removeEventListener('click', onDidIt, { capture: true } as EventListenerOptions);
  }, [phase, step]);

  // Leaving a celebrated interactive step: ask the owning page to quietly
  // undo the demo action (uncheck the task, remove the log entry).
  const maybeRevert = () => {
    const revertEvent = step?.interactive?.revertEvent;
    if (phase === 'celebrate' && revertEvent && doneDetail) {
      window.dispatchEvent(new CustomEvent(revertEvent, { detail: doneDetail }));
      setDoneDetail(null);
    }
  };

  // A guided-create can unmount the step's anchor and mount the celebration's
  // re-anchor moments LATER (e.g. area setup replaces the wizard with the
  // dashboard after a refetch). activeAnchor is computed at render time, so
  // poll briefly and force a re-render once the re-anchor exists.
  useEffect(() => {
    if (phase !== 'celebrate') return;
    const want = step?.interactive?.celebrate.anchor;
    if (!want || document.querySelector(`[data-tour="${want}"]`)) return;
    let tries = 0;
    const timer = window.setInterval(() => {
      if (document.querySelector(`[data-tour="${want}"]`) || ++tries > 20) {
        window.clearInterval(timer);
        forceTick((n) => n + 1);
      }
    }, 250);
    return () => window.clearInterval(timer);
  }, [phase, step]);

  // The element the frame currently points at: the step's anchor, or the
  // celebration's re-anchor when it exists on the page.
  const celebrateAnchor = step?.interactive?.celebrate.anchor;
  const activeAnchor =
    phase === 'celebrate' && celebrateAnchor && document.querySelector(`[data-tour="${celebrateAnchor}"]`)
      ? celebrateAnchor
      : step?.anchor;

  useEffect(() => {
    if (!activeAnchor) return;
    let raf = 0;
    let timer = 0;
    const query = () =>
      document.querySelector(`[data-tour="${activeAnchor}"]`) as HTMLElement | null;

    // The anchor's REAL scroll container: on desktop it's the window, but on
    // mobile the page scrolls inside .mobile-main — window.scrollTo is a
    // silent no-op there (the checklist celebration never scrolled into view
    // on phones because of exactly this).
    const scrollParentOf = (el: HTMLElement): HTMLElement | null => {
      let node = el.parentElement;
      while (node && node !== document.body) {
        const { overflowY } = getComputedStyle(node);
        if ((overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden') &&
            node.scrollHeight > node.clientHeight + 1) {
          return node;
        }
        node = node.parentElement;
      }
      return null; // window scrolls the page
    };

    // Tall elements (long task lists) must show their TOP, not their middle —
    // centering strands the user mid-list with the tooltip off screen.
    const centerOn = (el: HTMLElement) => {
      const raw = el.getBoundingClientRect();
      const isTall = raw.height > window.innerHeight * 0.55;
      const chrome = topChromeBottom();
      const offset = isTall ? chrome + 12 : Math.max(chrome, (window.innerHeight - raw.height) / 2);
      const scroller = scrollParentOf(el);
      if (scroller) {
        const cTop = scroller.getBoundingClientRect().top;
        scroller.scrollTo({ top: Math.max(0, scroller.scrollTop + (raw.top - cTop) - (offset - cTop)), behavior: 'smooth' });
      } else {
        window.scrollTo({ top: Math.max(0, window.scrollY + raw.top - offset), behavior: 'smooth' });
      }
    };

    const start = () => {
      const first = query();
      if (first) centerOn(first);

      // Leaving an interactive step reverts the demo action, which re-renders
      // the page and can shove this step's anchor off screen AFTER we've
      // scrolled. Once things settle, re-center if it's out of the viewport.
      timer = window.setTimeout(() => {
        const el = query();
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (r.top < topChromeBottom() || r.bottom > window.innerHeight - 12) centerOn(el);
      }, 900);

      // Glue the frame to the element on EVERY animation frame. Event-based
      // re-measurement (scroll/resize) lags behind momentum + nested scrolling,
      // which is what made the red frame drift. Re-query each frame too —
      // React re-renders can replace the node, and a detached element reports
      // a zeroed rect. If it's briefly missing, keep the last known frame.
      let prev = '';
      const tick = () => {
        const el = query();
        if (el) {
          const r = el.getBoundingClientRect();
          // Anchors that live INSIDE fixed chrome (the header hamburger, the
          // bottom mobile nav) must not be clamped — the clamp exists to stop
          // scrolling-content frames sliding under the header, but it shoves
          // frames for header/nav elements off their targets entirely.
          let fixedChrome = false;
          for (let n: HTMLElement | null = el; n && n !== document.body; n = n.parentElement) {
            if (getComputedStyle(n).position === 'fixed') { fixedChrome = true; break; }
          }
          const top = fixedChrome ? r.top : Math.max(r.top, topChromeBottom());
          const bottom = fixedChrome ? r.bottom : Math.min(r.bottom, window.innerHeight - 12);
          const nextRect = {
            top,
            left: r.left,
            width: r.width,
            height: Math.max(fixedChrome ? 24 : 40, bottom - top)
          };
          const key = `${nextRect.top}|${nextRect.left}|${nextRect.width}|${nextRect.height}`;
          if (key !== prev) { prev = key; setRect(nextRect); }
        }
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };

    // The anchor may not exist at this exact moment — e.g. the trash icon on a
    // task that the interactive demo checked off is unmounted until the revert
    // round-trips. Poll briefly instead of giving up on the first look.
    let tries = 0;
    const waitForAnchor = () => {
      if (query()) { start(); return; }
      if (++tries < 15) timer = window.setTimeout(waitForAnchor, 200);
      else setRect(null);
    };
    waitForAnchor();

    return () => { cancelAnimationFrame(raf); window.clearTimeout(timer); };
  }, [activeAnchor]);

  if (!available || !step || !rect) return null;

  const pad = 8;
  const isLast = index === available.length - 1;
  // Phase updates lag one render behind index changes (the reset effect runs
  // after render) — never trust phase alone on a step with no interactive.
  const awaiting = phase === 'await' && !!step.interactive;
  const celebrating = phase === 'celebrate' && !!step.interactive;
  const rectBottom = rect.top + rect.height;
  const spaceBelow = window.innerHeight - rectBottom;
  const spaceAbove = rect.top - topChromeBottom();
  // Below the frame if it fits, above if not, and pinned to the bottom of the
  // viewport (over the dimmed area) when the frame fills the whole screen.
  const tooltipPos =
    spaceBelow >= 190 ? { top: rectBottom + pad + 6 }
    : spaceAbove >= 190 ? { bottom: window.innerHeight - rect.top + pad + 6 }
    : { bottom: 16 };
  // Never let the tooltip exceed the viewport — 330 overflows small phones.
  const tooltipWidth = Math.min(TOOLTIP_WIDTH, window.innerWidth - 24);
  const left = Math.max(12, Math.min(rect.left, window.innerWidth - tooltipWidth - 12));

  const next = () => {
    maybeRevert();
    if (isLast) { onEnd?.(true); onClose(); }
    else setIndex(index + 1);
  };
  const back = () => index > 0 && setIndex(index - 1);
  const close = () => {
    maybeRevert();
    onEnd?.(false);
    onClose();
  };

  const title = applyCurrencyName(applyPlanTerm(celebrating ? step.interactive!.celebrate.title : step.title, planTerm), currency);
  const body = applyCurrencyName(applyPlanTerm(celebrating ? step.interactive!.celebrate.body : step.body, planTerm), currency);

  const holeTop = rect.top - pad;
  const holeLeft = rect.left - pad;
  const holeWidth = rect.width + pad * 2;
  const holeHeight = rect.height + pad * 2;

  return (
    <div
      className="fixed inset-0 z-[1100]"
      style={awaiting ? { pointerEvents: 'none' } : undefined}
      onClick={awaiting ? undefined : next}
      role="dialog"
      aria-label="Page tour"
    >
      {awaiting ? (
        // Interactive "your turn" step: NO dimming — just a pulsing frame
        // around the target. The whole page stays interactive so the action
        // (and any dialog it opens, like a create form) works normally.
        <>
          <div
            style={{
              position: 'fixed',
              top: holeTop,
              left: holeLeft,
              width: holeWidth,
              height: holeHeight,
              borderRadius: 12,
              border: '2px solid #E51636',
              pointerEvents: 'none',
              zIndex: 1100,
              animation: 'ldg-tour-pulse 1.6s ease-in-out infinite'
            }}
          />
          <style>{'@keyframes ldg-tour-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(229,22,54,0.5); } 50% { box-shadow: 0 0 0 12px rgba(229,22,54,0); } }'}</style>
        </>
      ) : (
        // Passive step or celebration: one big dim with the frame cut out.
        <div
          style={{
            position: 'fixed',
            top: holeTop,
            left: holeLeft,
            width: holeWidth,
            height: holeHeight,
            borderRadius: 12,
            boxShadow: '0 0 0 9999px rgba(15, 15, 15, 0.6)',
            border: '2px solid #E51636',
            pointerEvents: 'none',
            transition: 'top 200ms, left 200ms, width 200ms, height 200ms',
            zIndex: 1100
          }}
        />
      )}

      <div
        className="fixed bg-white rounded-xl shadow-2xl p-4"
        style={{ zIndex: 1101, width: tooltipWidth, left, pointerEvents: 'auto', ...tooltipPos }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <p className="font-semibold text-gray-900 text-sm">{title}</p>
          <button onClick={close} aria-label="Close tour" className="text-gray-400 hover:text-gray-600 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-gray-600 mt-1">{body}</p>

        {awaiting ? (
          <div className="flex items-center justify-between mt-3">
            <span className="flex items-center gap-2 text-sm font-medium text-[#E51636]">
              <span className="w-2 h-2 rounded-full bg-[#E51636] animate-pulse" />
              {step.interactive!.prompt}
            </span>
            <button className="text-xs text-gray-400 hover:text-gray-600" onClick={next}>
              Skip this
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between mt-3">
            <div className="flex gap-1">
              {available.map((_, i) => (
                <span
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full ${i === index ? 'bg-[#E51636]' : 'bg-gray-300'}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-1">
              {index > 0 && !celebrating && (
                <Button size="sm" variant="ghost" onClick={back}>
                  <ChevronLeft className="w-4 h-4" /> Back
                </Button>
              )}
              <Button size="sm" onClick={next}>
                {isLast ? "Got it — let me try" : <>Next <ChevronRight className="w-4 h-4" /></>}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
